import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  CheckCircle, Circle, ArrowRight, ArrowLeft, ShieldCheck, UsersFour, Buildings,
  GraduationCap, Trophy, Calendar as CalIcon, UploadSimple, FileText,
} from "@phosphor-icons/react";
import AddStaffDialog from "@/components/AddStaffDialog";
import UploadCredentialDialog from "@/components/UploadCredentialDialog";

const ORG_TYPES = [
  { id: "youth_club", label: "Youth Club", icon: Trophy, desc: "Community rec leagues, youth associations" },
  { id: "academy", label: "Academy", icon: GraduationCap, desc: "Player development academy or training center" },
  { id: "league", label: "League", icon: UsersFour, desc: "Multi-team competitive league" },
  { id: "school", label: "High School", icon: Buildings, desc: "High school or district athletic dept" },
  { id: "tournament", label: "Tournament", icon: CalIcon, desc: "Event or tournament organizer" },
];

const STEP_KEYS = ["org_details", "add_staff", "add_credentials", "review", "first_report"];
const STEP_LABELS = ["Organization", "Staff", "Credentials", "Review", "First Report"];

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [status, setStatus] = useState(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [orgName, setOrgName] = useState(user?.org_name || "");
  const [orgType, setOrgType] = useState(user?.org_type || "");
  const [staffOpen, setStaffOpen] = useState(false);
  const [credOpen, setCredOpen] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);

  const load = async () => {
    const { data } = await api.get("/onboarding/status");
    setStatus(data);
    setOrgName(data.org_name || "");
    setOrgType(data.org_type || "");
    setStepIdx(data.current_step || 0);
    const s = await api.get("/staff-with-status");
    setStaffList(s.data);
  };

  useEffect(() => { load(); }, []);

  const persistStep = async (extra = {}) => {
    await api.post("/onboarding/update", { step: stepIdx, ...extra });
  };

  const goNext = async () => {
    if (stepIdx === 0) {
      if (!orgName.trim() || !orgType) { toast.error("Enter organization name and choose type"); return; }
      await api.post("/onboarding/update", { org_name: orgName.trim(), org_type: orgType, step: 1 });
      setUser({ ...user, org_name: orgName.trim(), org_type: orgType });
      toast.success("Organization saved");
    } else {
      await persistStep({ step: stepIdx + 1 });
    }
    const next = Math.min(stepIdx + 1, STEP_KEYS.length - 1);
    setStepIdx(next);
    load();
  };

  const goBack = async () => {
    const prev = Math.max(stepIdx - 1, 0);
    setStepIdx(prev);
    await api.post("/onboarding/update", { step: prev });
  };

  const finish = async () => {
    await api.post("/onboarding/complete");
    toast.success("You're audit-ready.");
    navigate("/dashboard");
  };

  const skipForNow = async () => {
    await api.post("/onboarding/skip");
    toast.info("Onboarding skipped. Resume from Settings.");
    navigate("/dashboard");
  };

  const downloadFirstReport = async () => {
    try {
      const res = await api.get("/reports/compliance.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `first_report_${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("First audit-ready report downloaded");
    } catch (_) {
      toast.error("Report failed");
    }
  };

  if (!status) return <div className="min-h-screen grid place-items-center text-slate-400">Loading…</div>;

  const progress = status.progress_pct;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50 flex flex-col">
      <header className="border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-orange-500 grid place-items-center">
            <ShieldCheck size={20} weight="bold" color="#fff7ed" />
          </div>
          <div>
            <div className="font-display font-black leading-none">AuditReady <span className="text-orange-400">Sports</span></div>
            <div className="font-label text-slate-500 mt-1">GUIDED SETUP</div>
          </div>
        </div>
        <Button
          variant="ghost"
          data-testid="skip-onboarding-button"
          onClick={skipForNow}
          className="text-slate-400 hover:text-slate-100"
        >
          Skip for now
        </Button>
      </header>

      <div className="max-w-3xl w-full mx-auto px-4 md:px-6 py-8">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="font-label text-orange-400">SETUP PROGRESS</span>
            <span className="text-slate-400"><span data-testid="onboarding-progress-pct" className="text-slate-100 font-semibold">{progress}%</span> complete</span>
          </div>
          <Progress value={progress} className="h-2 bg-slate-800" />
          <div className="grid grid-cols-5 gap-2 mt-4">
            {STEP_KEYS.map((k, i) => {
              const done = status.steps[k];
              const active = i === stepIdx;
              return (
                <div key={k} className={`text-xs px-2 py-2 rounded-lg border ${active ? "border-orange-500/40 bg-orange-500/10 text-orange-300" : done ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-300" : "border-white/10 bg-slate-900 text-slate-500"}`}>
                  <div className="flex items-center gap-1.5 mb-1">
                    {done ? <CheckCircle size={13} weight="fill" /> : <Circle size={13} />}
                    <span className="font-label text-[10px]">STEP {i + 1}</span>
                  </div>
                  <div className="font-semibold text-slate-100 leading-tight text-[11px]">{STEP_LABELS[i]}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Step content */}
        <div className="rounded-2xl border border-white/10 bg-slate-900 p-6 md:p-8">
          {stepIdx === 0 && (
            <div data-testid="onboarding-step-org">
              <div className="font-label text-orange-400 mb-2">STEP 1 · ORGANIZATION</div>
              <h1 className="text-3xl font-display font-black tracking-tight mb-2">Tell us about your organization</h1>
              <p className="text-slate-400 mb-6">This appears on your audit reports and public badge.</p>

              <Label className="font-label text-slate-400">ORGANIZATION NAME</Label>
              <Input
                data-testid="onboarding-org-name"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                placeholder="Lincoln High Athletics"
                className="bg-slate-950 border-white/10 focus:border-orange-500 mt-1 mb-6"
              />

              <Label className="font-label text-slate-400 mb-2 block">ORGANIZATION TYPE</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {ORG_TYPES.map((t) => {
                  const active = orgType === t.id;
                  const Icon = t.icon;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      data-testid={`org-type-${t.id}`}
                      onClick={() => setOrgType(t.id)}
                      className={`text-left rounded-xl border p-4 transition-all ${active ? "border-orange-500/60 bg-orange-500/10 shadow-lg shadow-orange-500/10" : "border-white/10 bg-slate-950 hover:border-white/25"}`}
                    >
                      <Icon size={22} weight="bold" className={active ? "text-orange-400" : "text-slate-400"} />
                      <div className="mt-2 font-semibold text-slate-100">{t.label}</div>
                      <div className="text-xs text-slate-500 mt-1">{t.desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {stepIdx === 1 && (
            <div data-testid="onboarding-step-staff">
              <div className="font-label text-orange-400 mb-2">STEP 2 · STAFF ROSTER</div>
              <h1 className="text-3xl font-display font-black tracking-tight mb-2">Add or import your staff</h1>
              <p className="text-slate-400 mb-6">Coaches, athletic trainers, referees and volunteers.</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <button
                  type="button"
                  data-testid="onboarding-add-staff"
                  onClick={() => setStaffOpen(true)}
                  className="rounded-xl border border-white/10 bg-slate-950 p-5 text-left hover:border-orange-500/40 transition-colors"
                >
                  <UsersFour size={22} weight="bold" className="text-orange-400" />
                  <div className="mt-3 font-semibold">Add one at a time</div>
                  <div className="text-xs text-slate-500 mt-1">Fastest for the first few members</div>
                </button>
                <button
                  type="button"
                  data-testid="onboarding-goto-import"
                  onClick={() => navigate("/import")}
                  className="rounded-xl border border-white/10 bg-slate-950 p-5 text-left hover:border-orange-500/40 transition-colors"
                >
                  <UploadSimple size={22} weight="bold" className="text-orange-400" />
                  <div className="mt-3 font-semibold">Bulk import from CSV</div>
                  <div className="text-xs text-slate-500 mt-1">Upload a spreadsheet of staff + credentials</div>
                </button>
              </div>

              <div className="rounded-xl border border-white/10 bg-slate-950 p-4">
                <div className="font-label text-slate-500 mb-2">CURRENT ROSTER ({staffList.length})</div>
                {staffList.length === 0 ? (
                  <div className="text-sm text-slate-500">No staff yet.</div>
                ) : (
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {staffList.slice(0, 8).map((s) => (
                      <div key={s.id} className="flex items-center justify-between text-sm py-1.5">
                        <span className="text-slate-200">{s.name}</span>
                        <span className="text-xs text-slate-500 capitalize">{s.role}</span>
                      </div>
                    ))}
                    {staffList.length > 8 && <div className="text-xs text-slate-500 pt-2">+ {staffList.length - 8} more</div>}
                  </div>
                )}
              </div>
            </div>
          )}

          {stepIdx === 2 && (
            <div data-testid="onboarding-step-credentials">
              <div className="font-label text-orange-400 mb-2">STEP 3 · CREDENTIALS</div>
              <h1 className="text-3xl font-display font-black tracking-tight mb-2">Assign required credentials</h1>
              <p className="text-slate-400 mb-6">Upload certificates and set expiration dates. Or come back to it after import.</p>

              {staffList.length === 0 ? (
                <div className="rounded-xl border border-amber-500/25 bg-amber-500/5 p-4 text-sm text-amber-300">
                  Add at least one staff member first (Step 2).
                </div>
              ) : (
                <div className="space-y-2">
                  {staffList.map((s) => (
                    <div key={s.id} className="flex items-center justify-between rounded-xl border border-white/10 bg-slate-950 p-4">
                      <div>
                        <div className="font-semibold text-slate-100">{s.name}</div>
                        <div className="text-xs text-slate-500 capitalize">{s.role} · {s.credential_count} credential{s.credential_count === 1 ? "" : "s"} on file</div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => { setSelectedStaff(s); setCredOpen(true); }}
                        className="bg-orange-500 text-white hover:bg-orange-400 rounded-full"
                      >
                        <FileText size={14} weight="bold" className="mr-1.5" /> Upload
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {stepIdx === 3 && (
            <div data-testid="onboarding-step-review">
              <div className="font-label text-orange-400 mb-2">STEP 4 · REVIEW</div>
              <h1 className="text-3xl font-display font-black tracking-tight mb-2">Review missing or expiring items</h1>
              <p className="text-slate-400 mb-6">Any red or yellow items here will surface again on your dashboard.</p>

              <div className="rounded-xl border border-white/10 bg-slate-950 p-5">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="kpi-number text-emerald-300">{staffList.filter(s=>s.overall_status==="current").length}</div>
                    <div className="text-xs text-slate-400 mt-1">Compliant</div>
                  </div>
                  <div>
                    <div className="kpi-number text-amber-300">{staffList.filter(s=>s.overall_status==="expiring_soon").length}</div>
                    <div className="text-xs text-slate-400 mt-1">Expiring</div>
                  </div>
                  <div>
                    <div className="kpi-number text-rose-300">{staffList.filter(s=>s.overall_status==="expired" || s.overall_status==="none").length}</div>
                    <div className="text-xs text-slate-400 mt-1">Needs attention</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {stepIdx === 4 && (
            <div data-testid="onboarding-step-report">
              <div className="font-label text-orange-400 mb-2">STEP 5 · FIRST AUDIT REPORT</div>
              <h1 className="text-3xl font-display font-black tracking-tight mb-2">Generate your first report</h1>
              <p className="text-slate-400 mb-6">Download an audit-ready PDF and you're done with setup.</p>

              <div className="rounded-xl border border-orange-500/25 bg-orange-500/5 p-6 text-center">
                <FileText size={40} weight="duotone" className="text-orange-400 mx-auto mb-3" />
                <div className="font-semibold text-slate-100 mb-1">AuditReady Sports · Compliance Report</div>
                <div className="text-xs text-slate-400 mb-4">Includes org summary, per-credential detail, and disclaimer.</div>
                <Button
                  data-testid="onboarding-download-report"
                  onClick={downloadFirstReport}
                  className="bg-orange-500 text-white hover:bg-orange-400 font-semibold rounded-full"
                >
                  Download First Report
                </Button>
              </div>
            </div>
          )}

          {/* Nav */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/10">
            <Button
              variant="ghost"
              onClick={goBack}
              disabled={stepIdx === 0}
              className="text-slate-400 hover:text-slate-100 disabled:opacity-40"
            >
              <ArrowLeft size={14} weight="bold" className="mr-1.5" /> Back
            </Button>
            {stepIdx < STEP_KEYS.length - 1 ? (
              <Button
                data-testid="onboarding-next-button"
                onClick={goNext}
                className="bg-orange-500 text-white hover:bg-orange-400 font-semibold rounded-full px-5"
              >
                Continue <ArrowRight size={14} weight="bold" className="ml-1.5" />
              </Button>
            ) : (
              <Button
                data-testid="onboarding-finish-button"
                onClick={finish}
                className="bg-orange-500 text-white hover:bg-orange-400 font-semibold rounded-full px-5"
              >
                Finish Setup <CheckCircle size={14} weight="bold" className="ml-1.5" />
              </Button>
            )}
          </div>
        </div>

        <p className="text-center text-slate-600 text-xs mt-6">
          You can skip and return to setup anytime from Settings.
        </p>
      </div>

      <AddStaffDialog open={staffOpen} onOpenChange={setStaffOpen} onCreated={load} />
      <UploadCredentialDialog
        open={credOpen}
        onOpenChange={setCredOpen}
        staff={selectedStaff}
        onCreated={load}
      />
    </div>
  );
}
